<script>
import UserResource from '@/resources/user'
import PaymentMethodForm from './PaymentMethod.vue'
import messages from '@/mixins/messages'
import { IS_DEV } from '@/config'
import { randomStringGenerate } from '@/util'

export default {
    mixins: [messages],
    components: {
        PaymentMethodForm
    },
    props: {},
    data() {
        return {
            email: IS_DEV ? (randomStringGenerate() + '@test.com') : '',
            password: IS_DEV ? 'test123test123' : '',
            passwordConfirm: IS_DEV ? 'test123test123' : '',
            isFocusingOnPassword: false,
            tempPassword: '',
            firstName: IS_DEV ? 'Lewis' : '',
            lastName: IS_DEV ? 'Racer' : '',
            phone: '',
            zipCode: '',
            vMake: '',
            vModel: '',
            vYear: '',
            vIsSUV: false,
            vIsLarge: false,
            loading: false,
            step: 0,
            userResource: null,
            paymentProfile: null,
            optInAccount: false,
            optInMarketing: false
        }
    },
    computed: {
        headerText() {
            switch(this.step) {
                case 0:
                default:
                    return 'Sign Up'
                case 1:
                    return 'Profile Details'
                case 2:
                    return 'Payment Method'
            }
        },
        phoneSanitized() {
            return this.phone.replace(/[^+\d]+/g, '')
        },
        isValidEmail() {
            var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(this.email).toLowerCase());
        },
        isValidPhone() {
            return this.phoneSanitized.length > 9
        },
    },
    watch: {
        step() {
            this.$emit('step', this.step)
        }
    },
    methods: {
        onSubmit() {
            switch(this.step) {
                case 0:
                    return this.registerEmail()
                case 1:
                    return this.registerDetails()
                case 2:
                    if (!this.paymentProfile) {
                        return this.registerPaymentProfile()
                    } else {
                        this.$router.push({ name: 'reservations' })
                    }
            }
        },
        async registerEmail() {
            this.userResource = new UserResource({
                username: this.email,
                email: this.email,
                password: this.tempPassword
            })
            
            try {
                this.loading = true
                await this.userResource.save()
                let loginResult = await this.$store.dispatch('login', { username: this.email, password: this.tempPassword })
                this.userResource = loginResult.userResource
                this.step = 1
                this.$analytics.identify()
                this.$analytics.track('created_account')
            } catch(e) {
                if (e.name === 'AttributeError' && this.$store.getters.userId) {
                    this.step = 1
                } else if (e.response && e.response.data['username']) {
                    let message = e.response.data['username'].join(' ')

                    if(message.match(/^A user with that username already exists/)) {
                        await UserResource.forgotPassword(this.email)
                        message += ' We\'ve sent you a login link to your email address. Or, you can <a href="/login">Log In</a> instead'
                        this.addMessage('info', message, 1e4)
                    } else {
                        this.addMessage('danger', message)
                    }
                } else if (e.response && e.response.status == 400) {
                    this.addMessage('danger', 'Registration failed, please check details and try agian')
                } else {
                    this.addMessage('danger', e.message || 'An unexpected error occurred, please reload this page and try again')
                    throw e
                }
            } finally {
                this.loading = false
            }
        },
        async registerDetails() {
            let profileSaved = false
            try {
                this.loading = true
                await this.userResource.update()
                
                await Promise.all([
                    this.userResource.setPassword(this.password),
                    this.$store.dispatch('getLocationData')
                ])
                
                this.userResource.set('first_name', this.firstName)
                this.userResource.set('last_name', this.lastName)
                this.userResource.set('phone', this.phone)
                this.userResource.set('zip_code', this.zipCode)
                this.userResource.set('opt_in_account_updates', this.optInAccount)
                this.userResource.set('opt_in_marketing_updates', this.optInMarketing)
                await this.userResource.save()
                profileSaved = true
                this.$analytics.track('onboarded')
                return this.$router.push(this.getSuccessRoute())
            } catch(e) {
                if (e.name === 'AttributeError' && profileSaved) {
                    return this.$router.push(this.getSuccessRoute())
                }
                this.axiosExtract(e)
            } finally {
                this.loading = false
            }
        },
        registerPaymentProfile() {
            let paymentComponent = this.$refs.paymentForm
            paymentComponent.addCard()
        },
        setPaymentProfile(payment) {
            this.paymentProfile = payment.card
            this.$router.push(this.getSuccessRoute())
        },
        isValid() {
            if(this.loading) {
                return false
            }

            if(this.step == 0) {
                return this.email && this.isValidEmail
            } else if(this.step == 1) {
                return this.password == this.passwordConfirm && this.firstName && this.lastName && this.isValidPhone && this.optInAccount
            } else {
                return true
            }
        },
        getSuccessRoute() {
            if (this.$route.query?.redirect) {
                return this.$route.query?.redirect
            }

            return { name: 'reservations' }
        }
    },
    async mounted() {
        this.tempPassword = randomStringGenerate()

        if(this.$route.query.step) {
            this.$nextTick(async () => {
                this.userResource = await this.$store.dispatch('getUserResource')
                this.step = this.$route.query.step
            })
        }
    }
}
</script>

<template>
    <b-form class="horizontal" @submit.prevent="{}">
        <span v-if="step == 0">
            <b-form-group label="Dirección de Correo Electrónico:">
                <b-form-input type="email" name="email" v-model="email" autocomplete="email" />
            </b-form-group>
            <hr />
            <messages />
            <b-button type="submit" @click="registerEmail" :disabled="!isValid()">Siguiente Paso</b-button>
            <b-button variant="link" class="float-right" :to="{ name: 'login', query: $route.query }" size="sm">¿Ya tienes una cuenta?</b-button>
        </span>

        <span v-if="step == 1">
            <b-row>
                <b-col>
                    <b-form-group label="Nombre:">
                        <b-form-input type="text" name="first_name" v-model="firstName" autocomplete="new-password" />
                    </b-form-group>
                    <b-form-group label="Apellido:">
                        <b-form-input type="text" name="last_name" v-model="lastName" autocomplete="new-password" />
                    </b-form-group>
                    <b-form-group label="Número de Móvil:">
                        <b-form-input type="text" name="phone" v-model="phone" autocomplete="new-password" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Elige una Contraseña:">
                        <b-form-input type="password" v-model="password" @focus="isFocusingOnPassword = true" @blur="isFocusingOnPassword = false" autocomplete="new-password" />
                    </b-form-group>
                    <b-form-group label="Confirma la Contraseña:">
                        <b-form-input type="password" v-model="passwordConfirm" @focus="isFocusingOnPassword = true" @blur="isFocusingOnPassword = false" autocomplete="new-password" />
                    </b-form-group>
                    <b-alert variant="primary" :show="isFocusingOnPassword">La contraseña debe tener 8 caracteres o más</b-alert>
                    <b-alert variant="warning" :show="!!password && password != passwordConfirm">Las contraseñas deben coincidir</b-alert>
                </b-col>
            </b-row>
            <hr />
            <div>
                <small>
                    <b-form-checkbox v-model="optInAccount" switch>
                        Al marcar esta casilla, consiento recibir mensajes transaccionales relacionados con mi cuenta, pedidos o servicios que he solicitado. Estos mensajes pueden incluir recordatorios de citas, confirmaciones de pedidos y notificaciones de cuenta, entre otros. La frecuencia de los mensajes puede variar. Pueden aplicarse tarifas de Mensaje y Datos. Responde AYUDA para obtener ayuda o STOP para cancelar.
                    </b-form-checkbox>
                    <b-form-checkbox v-model="optInMarketing" class="mt-3" switch>
                        Al marcar esta casilla, consiento recibir mensajes de marketing y promocionales, incluidas ofertas especiales, descuentos, actualizaciones de nuevos productos, entre otros. La frecuencia de los mensajes puede variar. Pueden aplicarse tarifas de Mensaje y Datos. Responde AYUDA para obtener ayuda o STOP para cancelar.
                    </b-form-checkbox>
                </small>
            </div>
            <b-row>
                <b-col>
                    <hr />
                    <messages />
                    <b-button type="submit" @click="registerDetails" :disabled="!isValid()">Siguiente Paso</b-button>
                </b-col>
            </b-row>
        </span>

        <span v-if="step == 2">
            <b-alert variant="success" show><b-icon icon="lock-fill" /> Tu tarjeta no será cargada aún</b-alert>
            <payment-method-form
                :userResource="userResource"
                @success="setPaymentProfile"
                @loading="(status) => this.loading = status"
                cols-lg="12" 
                ref="paymentForm">
                <template slot="before-submit-button">
                    <hr />
                </template>
                <template slot="after-submit-button-text">
                    <b-button variant="link" :to="getSuccessRoute()">omitir este paso</b-button>
                </template>
                <template slot="after-update-payment-method-link">
                    <hr />
                    <b-button type="submit" @click="step = 3">Siguiente Paso</b-button>
                </template>
            </payment-method-form>
        </span>
    </b-form>
</template>

<style lang="scss" scoped>
@media (max-width: 767.98px) {
    #nav-items {
        display: none;
    }

    #signup-head {
        display: block !important;
    }
}
</style>